package com.evoapps.evoshift.viewmodels

import androidx.lifecycle.ViewModel
import androidx.lifecycle.viewModelScope
import com.evoapps.evoshift.domain.model.AvailabilityLinkException
import com.evoapps.evoshift.domain.model.AvailabilityLinkForm
import com.evoapps.evoshift.domain.model.AvailabilityStatus
import com.evoapps.evoshift.domain.model.DayPreference
import com.evoapps.evoshift.domain.model.HoursRange
import com.evoapps.evoshift.domain.repository.AvailabilityLinkRepository
import com.evoapps.evoshift.ui.util.UiText
import com.evoapps.evoshift.ui.util.toUiText
import evoshift.composeapp.generated.resources.Res
import evoshift.composeapp.generated.resources.availability_link_load_failed
import kotlinx.coroutines.flow.MutableStateFlow
import kotlinx.coroutines.flow.StateFlow
import kotlinx.coroutines.flow.asStateFlow
import kotlinx.coroutines.flow.update
import kotlinx.coroutines.launch
import kotlinx.datetime.LocalDate

data class AvailabilityLinkUiState(
    val isLoading: Boolean = true,
    /** The link does not work (unknown, expired, revoked, replaced): one message for all. */
    val invalid: Boolean = false,
    /** Loading failed for another reason (offline, too many attempts): "Try again". */
    val loadError: UiText? = null,
    val form: AvailabilityLinkForm? = null,
    /** The month being edited: every day of the month, on the workplace's slots. */
    val days: Map<Int, DayPreference> = emptyMap(),
    val selectedDay: Int? = null,
    val isSending: Boolean = false,
    /** Sent in this visit: the thank-you, with "Change my answer". */
    val sent: Boolean = false,
    /** Sending failed (shown on the form, which keeps the answers). */
    val error: UiText? = null
) {
    /** The managers locked the month: shown, not changeable. */
    val isReadOnly: Boolean get() = form?.locked == true || isSending

    /** Days of the month asked for (SH-48); null for the whole month. */
    val requestedDays: IntRange? get() {
        val period = form?.period ?: return null
        val start = period.startDate.take(10)
        val end = period.endDate.take(10)
        val month = form.month
        if (!start.startsWith(month) || !end.startsWith(month)) return null
        val range = start.takeLast(2).toInt()..end.takeLast(2).toInt()
        return range.takeUnless { it.first == 1 && it.last == linkMonthDays(month) }
    }

    fun isRequested(day: Int): Boolean = requestedDays?.contains(day) ?: true
}

sealed interface AvailabilityLinkEvent {
    data object Retry : AvailabilityLinkEvent
    data class SelectDay(val day: Int) : AvailabilityLinkEvent
    data class UpdateDay(val day: Int, val preference: DayPreference) : AvailabilityLinkEvent
    /** The hours of a day (null: the whole day). */
    data class SetHours(val day: Int, val hours: HoursRange?) : AvailabilityLinkEvent
    data object Submit : AvailabilityLinkEvent
    /** After sending: back to the form to change the answer. */
    data object ChangeAnswer : AvailabilityLinkEvent
    data object DismissError : AvailabilityLinkEvent
}

/**
 * SH-173: the web form behind an e-mailed availability link (`/d/<token>`, no app, no sign-in):
 * the person's month on the workplace's slots, sent with one button. The token is the only key;
 * the form never leaves this one month.
 */
class AvailabilityLinkViewModel(
    private val token: String,
    private val repository: AvailabilityLinkRepository
) : ViewModel() {
    private val _uiState = MutableStateFlow(AvailabilityLinkUiState())
    val uiState: StateFlow<AvailabilityLinkUiState> = _uiState.asStateFlow()

    init {
        load()
    }

    fun onEvent(event: AvailabilityLinkEvent) {
        when (event) {
            AvailabilityLinkEvent.Retry -> load()
            is AvailabilityLinkEvent.SelectDay -> _uiState.update { it.copy(selectedDay = event.day) }
            is AvailabilityLinkEvent.UpdateDay -> edit(event.day) { event.preference }
            is AvailabilityLinkEvent.SetHours -> edit(event.day) { it.copy(hours = listOfNotNull(event.hours)) }
            AvailabilityLinkEvent.Submit -> submit()
            AvailabilityLinkEvent.ChangeAnswer -> _uiState.update { it.copy(sent = false) }
            AvailabilityLinkEvent.DismissError -> _uiState.update { it.copy(error = null) }
        }
    }

    private fun edit(day: Int, change: (DayPreference) -> DayPreference) {
        _uiState.update { state ->
            if (state.isReadOnly || state.form == null) return@update state
            val current = state.days[day] ?: availableDay()
            state.copy(days = state.days + (day to change(current)))
        }
    }

    private fun load() {
        _uiState.update { it.copy(isLoading = true, loadError = null) }
        viewModelScope.launch {
            repository.form(token).fold(
                onSuccess = { form ->
                    val all = (1..linkMonthDays(form.month)).associateWith { form.days[it] ?: availableDay() }
                    _uiState.update {
                        it.copy(isLoading = false, invalid = false, form = form, days = all, selectedDay = it.selectedDay ?: firstRequested(form))
                    }
                },
                onFailure = { e ->
                    val invalid = e is AvailabilityLinkException && e.reason == AvailabilityLinkException.Reason.INVALID
                    _uiState.update {
                        it.copy(
                            isLoading = false,
                            invalid = invalid,
                            loadError = when {
                                invalid -> null
                                e is AvailabilityLinkException -> e.toUiText()
                                else -> UiText.Resource(Res.string.availability_link_load_failed)
                            }
                        )
                    }
                }
            )
        }
    }

    private fun submit() {
        val state = _uiState.value
        if (state.isReadOnly || state.form == null) return
        _uiState.update { it.copy(isSending = true, error = null) }
        viewModelScope.launch {
            repository.submit(token, state.days).fold(
                onSuccess = {
                    _uiState.update { it.copy(isSending = false, sent = true, form = it.form?.copy(submitted = true)) }
                },
                onFailure = { e ->
                    val invalid = e is AvailabilityLinkException && e.reason == AvailabilityLinkException.Reason.INVALID
                    val locked = e is AvailabilityLinkException && e.reason == AvailabilityLinkException.Reason.LOCKED
                    _uiState.update {
                        it.copy(
                            isSending = false,
                            invalid = invalid,
                            form = if (locked) it.form?.copy(locked = true) else it.form,
                            error = if (invalid) null else e.toUiText()
                        )
                    }
                }
            )
        }
    }

    private fun availableDay() = DayPreference(AvailabilityStatus.AVAILABLE, null, null)

    private fun firstRequested(form: AvailabilityLinkForm): Int =
        form.period?.startDate?.take(10)?.takeIf { it.startsWith(form.month) }?.takeLast(2)?.toIntOrNull() ?: 1
}

/** Days of a "YYYY-MM" month (28 when unreadable). */
internal fun linkMonthDays(month: String): Int = runCatching {
    val (year, number) = month.split("-").map { it.toInt() }
    val first = LocalDate(year, number, 1)
    val next = if (number == 12) LocalDate(year + 1, 1, 1) else LocalDate(year, number + 1, 1)
    (next.toEpochDays() - first.toEpochDays()).toInt()
}.getOrDefault(28)
